import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import CurrentFacilityAtoms from '@/state/current-facility.state';

/**
 * Persisted UXDS version rollback selections for dev preview.
 * Scoped per facility; cleared automatically when the facility changes.
 */
export type UxdsVersionRollbackStorage = {
  facilityShortname: string;
  processViewVersion?: number;
  facilityMapVersion?: number;
} | null;

const uxdsVersionRollbackStorageBaseAtom = atomWithStorage<UxdsVersionRollbackStorage>(
  'uxds-version-rollback',
  null
);

/**
 * TODO: replace namespace-specific atoms with a more generic approach:
 * https://crossnokaye.atlassian.net/browse/UX-4093
 */
export const uxdsVersionRollbackStorageAtom = atom(
  (get) => {
    const storage = get(uxdsVersionRollbackStorageBaseAtom);
    const currentFacility = get(CurrentFacilityAtoms.facility);

    if (
      storage &&
      currentFacility?.shortName &&
      storage.facilityShortname !== currentFacility.shortName
    ) {
      return null;
    }

    return storage;
  },
  (_get, set, newValue: UxdsVersionRollbackStorage) => {
    set(uxdsVersionRollbackStorageBaseAtom, newValue);
  }
);
uxdsVersionRollbackStorageAtom.debugLabel = 'uxdsVersionRollbackStorageAtom';

export const processViewRollbackVersionAtom = atom<number | undefined>((get) => {
  const storage = get(uxdsVersionRollbackStorageAtom);
  return storage?.processViewVersion;
});
processViewRollbackVersionAtom.debugLabel = 'processViewRollbackVersionAtom';

export const facilityMapRollbackVersionAtom = atom<number | undefined>((get) => {
  const storage = get(uxdsVersionRollbackStorageAtom);
  return storage?.facilityMapVersion;
});
facilityMapRollbackVersionAtom.debugLabel = 'facilityMapRollbackVersionAtom';

export const isProcessViewRollbackPreviewAtom = atom(
  (get) => get(processViewRollbackVersionAtom) != null
);
isProcessViewRollbackPreviewAtom.debugLabel = 'isProcessViewRollbackPreviewAtom';

export const isFacilityMapRollbackPreviewAtom = atom(
  (get) => get(facilityMapRollbackVersionAtom) != null
);
isFacilityMapRollbackPreviewAtom.debugLabel = 'isFacilityMapRollbackPreviewAtom';

/**
 * True when a rollback version is active for any namespace.
 * For edit/save gating, prefer the namespace-specific preview atoms above.
 */
export const hasUxdsVersionRollbackAtom = atom((get) => {
  const storage = get(uxdsVersionRollbackStorageAtom);
  return storage?.processViewVersion != null || storage?.facilityMapVersion != null;
});
hasUxdsVersionRollbackAtom.debugLabel = 'hasUxdsVersionRollbackAtom';

export const UXDS_ROLLBACK_EDIT_DISABLED_TOOLTIP =
  'Clear version rollback in Developer Tools to edit';
